import React, { useState, useEffect, useCallback } from 'react';
import cn from 'classnames';
import cogoToast from 'cogo-toast';
import { confirmAlert } from 'react-confirm-alert';
import { opportunities } from '@shopmy/universal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle, faChevronRight } from '@fortawesome/pro-light-svg-icons';
import './OpportunityStrategyBillingCard.scss';

import Tooltip from '../General/Tooltip';
import { getBrandOpportunityStrategy, updateBrandOpportunityStrategy } from '../../api/brands';
import { formatPaymentMethod, formatBillingDate } from '../../helpers/brand_billing_helpers';

const COLLECTION_OPTIONS = [
  { value: 'auto_charge', label: 'Auto-charge' },
  { value: 'send_invoice', label: 'Email invoice' }
];
const PAYOUT_OPTIONS = [
  { value: 45, label: '45 days' },
  { value: 90, label: '90 days' },
  { value: 0, label: 'Same-week' }
];
const NUMBER_FIELDS = ['netPaymentTermsDays', 'payoutDelayDays', 'feePercentage', 'monthlyLimitAmount'];
const FIELDS = ['stripePaymentMethodId', 'invoiceCollection', 'paysThroughThirdPartyNetwork', ...NUMBER_FIELDS];

const OpportunityStrategyBillingCard = props => {
  const { brand } = props;
  const [strategy, setStrategy] = useState(null);
  const [draft, setDraft] = useState(toDraft(null));
  const [approvalReference, setApprovalReference] = useState('');
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const load = useCallback(async () => {
    const next = await getBrandOpportunityStrategy(brand.id);
    setStrategy(next);
    setDraft(toDraft(next.settings));
  }, [brand]);

  useEffect(() => {
    setStrategy(null);
    setError(null);
    load().catch(e => setError(getResponseErrorMessage(e, 'Could not load Strategies billing.')));
  }, [load]);

  const settings = strategy?.settings;
  const period = strategy?.period || {};
  const changes = {};
  FIELDS.forEach(field => {
    const next = toSetting(field, draft[field]);
    if (next !== toSetting(field, settings?.[field])) changes[field] = next;
  });
  const isDirty = !!Object.keys(changes).length;
  const needsApprovalReference =
    changes.feePercentage !== undefined || changes.invoiceCollection === 'send_invoice' || changes.paysThroughThirdPartyNetwork === true;
  const isActive = opportunities.isActiveStrategySettings(settings);
  const isPaused = isActive && !!settings.billingPausedAt;
  const activationBlockers = getActivationBlockers({
    settings,
    changes,
    hasUnpaidFrontedBudgetInvoice: strategy?.hasUnpaidFrontedBudgetInvoice,
    brand
  });

  let status;
  if (!settings?.activatedAt) status = 'Inactive';
  else if (settings.deactivatedAt) status = `Deactivated ${formatBillingDate(settings.deactivatedAt)}`;
  else if (settings.billingPausedAt) status = `Paused since ${formatBillingDate(settings.billingPausedAt)}`;
  else status = `Active since ${formatBillingDate(settings.activatedAt)}`;

  const setField = (field, value) => setDraft(prev => ({ ...prev, [field]: value }));

  const save = async (updates, successMessage) => {
    setIsSaving(true);
    setError(null);
    try {
      await updateBrandOpportunityStrategy(brand.id, needsApprovalReference ? { ...updates, approvalReference } : updates);
      cogoToast.success(successMessage);
      setApprovalReference('');
      await load();
    } catch (e) {
      const message = getResponseErrorMessage(e, 'Could not save that.');
      setError(message);
      cogoToast.error(message);
    }
    setIsSaving(false);
  };

  // Going live, stopping, and lifting a payment hold each move real money, so none of them lands on one tap.
  // Unsaved edits ride along so the operator can finish the setup and activate in one save.
  const confirmAction = (action, { title, message, label, success }) =>
    confirmAlert({
      title,
      message,
      buttons: [
        { label: 'Cancel', onClick: () => {}, className: 'cancel' },
        { label, onClick: () => save({ ...changes, [action]: true }, success) }
      ]
    });

  const activate = () =>
    confirmAction('activate', {
      title: `Activate ${brand.name} on Strategies?`,
      message: `${brand.name} is invoiced in arrears on the first business day of each month for the prior month's completed bonus spend, and new acceptances count against the monthly limit from now on.${isDirty ? ' Unsaved changes on this card are saved with it.' : ''}`, // prettier-ignore
      label: 'Activate',
      success: 'Activated.'
    });

  const deactivate = () =>
    confirmAction('deactivate', {
      title: `Deactivate ${brand.name} on Strategies?`,
      message: `${brand.name} stops taking new Strategies commitments. Spend already completed still bills on the next invoice.`,
      label: 'Deactivate',
      success: 'Deactivated.'
    });

  const unpause = () =>
    confirmAction('unpause', {
      title: 'Lift the billing pause?',
      message: `${brand.name} can send opportunities and take acceptances again even if the failed invoice is still unpaid.`,
      label: 'Unpause',
      success: 'Unpaused.'
    });

  return (
    <div className={cn('brand-billing-card brand-billing-strategy', { urgent: isPaused })}>
      <details className='brand-billing-disclosure' open>
        <summary className='brand-billing-summary'>
          <div className='brand-billing-summary-copy'>
            <div className='brand-billing-summary-heading'>
              <h2>Opportunities strategies</h2>
              <span className={cn('brand-billing-status', { warning: isPaused || !!error })}>
                {strategy ? (isPaused ? 'Paused' : isActive ? 'Active' : 'Inactive') : error ? 'Unavailable' : 'Loading…'}
              </span>
              {!!strategy && isDirty && <span className='brand-billing-status warning'>Unsaved changes</span>}
            </div>
            {!!settings && (
              <div className='brand-billing-summary-description'>
                {COLLECTION_OPTIONS.find(option => option.value === settings.invoiceCollection)?.label || 'Auto-charge'}
                {settings.feePercentage != null && <> · {Number(settings.feePercentage)}% fee</>}
                {settings.payoutDelayDays != null && (
                  <> · {Number(settings.payoutDelayDays) === 0 ? 'Same-week payouts' : `${settings.payoutDelayDays}-day payout delay`}</>
                )}
              </div>
            )}
          </div>
          <FontAwesomeIcon className='brand-billing-chevron' icon={faChevronRight} />
        </summary>
        <div className='brand-billing-card-body'>
          <div className='brand-billing-section-note'>
            Invoiced monthly in arrears for completed Strategy opportunities. The brand's own Plans keep prepaid budget.
            {!!strategy && <span className='brand-billing-strategy-history'>{status}</span>}
          </div>
          {!!strategy && (
            <>
              {!!settings && (
                <div className='brand-billing-strategy-usage'>
                  <div>
                    <span>Committed this month</span>
                    <strong>{formatAmount(period.committedAmount)}</strong>
                  </div>
                  <div>
                    <span>Completed this month</span>
                    <strong>{formatAmount(period.completedAmount)}</strong>
                  </div>
                  <div>
                    <span>Remaining allowance</span>
                    <strong>{formatAmount(period.remainingAmount)}</strong>
                  </div>
                </div>
              )}
              <div className='brand-billing-strategy-fields'>
                <div className='brand-billing-strategy-field'>
                  <span>Payment method</span>
                  <select
                    className='brand-billing-strategy-select'
                    aria-label='Strategies payment method'
                    value={draft.stripePaymentMethodId}
                    disabled={isSaving}
                    onChange={e => setField('stripePaymentMethodId', e.target.value)}
                  >
                    <option value=''>none</option>
                    {strategy.paymentMethods.map(pm => (
                      <option key={pm.id} value={pm.id}>
                        {formatPaymentMethod(pm)} · {pm.id}
                      </option>
                    ))}
                  </select>
                </div>

                <div className='brand-billing-strategy-field'>
                  <span>Collection</span>
                  <span className='brand-billing-strategy-controls'>
                    <select
                      className='brand-billing-strategy-select'
                      aria-label='Strategies collection'
                      value={draft.invoiceCollection}
                      disabled={isSaving}
                      onChange={e => setField('invoiceCollection', e.target.value)}
                    >
                      {COLLECTION_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    {draft.invoiceCollection === 'send_invoice' && (
                      <>
                        <input
                          className='brand-billing-strategy-input'
                          type='number'
                          min={1}
                          aria-label='Strategies net terms in days'
                          value={draft.netPaymentTermsDays}
                          disabled={isSaving}
                          onChange={e => setField('netPaymentTermsDays', e.target.value)}
                        />
                        <span>days net</span>
                      </>
                    )}
                  </span>
                </div>

                <div className='brand-billing-strategy-field'>
                  <span>Payout option</span>
                  <span className='brand-billing-strategy-controls'>
                    <select
                      className='brand-billing-strategy-select'
                      aria-label='Creator payout timing'
                      value={draft.payoutDelayDays}
                      disabled={isSaving}
                      onChange={e => setField('payoutDelayDays', e.target.value)}
                    >
                      <option value='' disabled>
                        Choose…
                      </option>
                      {PAYOUT_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <label className='brand-billing-strategy-checkbox'>
                      <input
                        type='checkbox'
                        checked={draft.paysThroughThirdPartyNetwork}
                        disabled={isSaving}
                        onChange={e => setField('paysThroughThirdPartyNetwork', e.target.checked)}
                      />
                      third-party network
                    </label>
                  </span>
                </div>

                <div className='brand-billing-strategy-field'>
                  <span>Monthly limit</span>
                  <span className='brand-billing-strategy-controls'>
                    <span>$</span>
                    <input
                      className='brand-billing-strategy-input'
                      type='number'
                      min={0}
                      aria-label='Monthly limit in dollars'
                      value={draft.monthlyLimitAmount}
                      disabled={isSaving}
                      onChange={e => setField('monthlyLimitAmount', e.target.value)}
                    />
                  </span>
                </div>

                <div className='brand-billing-strategy-field'>
                  <div className='brand-billing-strategy-fee-label'>
                    Fee
                    <Tooltip message='<div class="brand-billing-strategy-fee-tooltip">Standard Pricing<br /><br />• Base fee: 12%<br />• Total fee with instant creator payouts: 15%</div>'>
                      <FontAwesomeIcon icon={faInfoCircle} title='Standard Pricing' />
                    </Tooltip>
                  </div>
                  <span className='brand-billing-strategy-controls'>
                    <input
                      className='brand-billing-strategy-input'
                      type='number'
                      min={0}
                      max={100}
                      step='0.01'
                      aria-label='Fee percentage'
                      value={draft.feePercentage}
                      disabled={isSaving}
                      onChange={e => setField('feePercentage', e.target.value)}
                    />
                    <span>%</span>
                  </span>
                </div>

                {needsApprovalReference && (
                  <div className='brand-billing-strategy-field'>
                    <span>Approval reference</span>
                    <input
                      className='brand-billing-strategy-text'
                      aria-label='Approval reference'
                      value={approvalReference}
                      placeholder='Slack thread or ticket'
                      disabled={isSaving}
                      onChange={e => setApprovalReference(e.target.value)}
                    />
                  </div>
                )}
              </div>

              <div className='brand-billing-strategy-actions'>
                <button
                  className='brand-billing-strategy-button primary'
                  disabled={!isDirty || isSaving}
                  onClick={() => save(changes, isActive ? 'Saved.' : 'Saved. Strategies stays inactive until you click Activate.')}
                >
                  {isSaving ? 'Saving…' : 'Save changes'}
                </button>

                {isActive ? (
                  <button className='brand-billing-strategy-button destructive' disabled={isSaving} onClick={deactivate}>
                    Deactivate
                  </button>
                ) : (
                  <button className='brand-billing-strategy-button' disabled={isSaving || !!activationBlockers.length} onClick={activate}>
                    Activate
                  </button>
                )}
                {!isActive && !!activationBlockers.length && (
                  <span className='brand-billing-strategy-blockers'>Activation needs: {activationBlockers.join(', ')}.</span>
                )}
                {isPaused && (
                  <button className='brand-billing-strategy-button' disabled={isSaving} onClick={unpause}>
                    Unpause
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </details>
      {!!error && (
        <div className='brand-billing-callout' role='alert'>
          {error}
        </div>
      )}
      {!!isPaused && <div className='brand-billing-callout'>Billing is paused. Review Strategies settings before lifting the pause.</div>}
      {!!strategy?.hasUnpaidFrontedBudgetInvoice && !isActive && (
        <div className='brand-billing-callout'>Has an unpaid fronted budget invoice, so cannot be activated until it is settled.</div>
      )}
    </div>
  );
};

const toDraft = settings => ({
  stripePaymentMethodId: settings?.stripePaymentMethodId || '',
  invoiceCollection: settings?.invoiceCollection || 'auto_charge',
  paysThroughThirdPartyNetwork: !!settings?.paysThroughThirdPartyNetwork,
  netPaymentTermsDays: settings?.netPaymentTermsDays ?? '',
  payoutDelayDays: settings?.payoutDelayDays ?? '',
  feePercentage: settings?.feePercentage ?? '',
  monthlyLimitAmount: settings?.monthlyLimitAmount ?? ''
});

// Inputs hold strings and MySQL hands decimals back as strings, so both sides are read the way the server will store them
// Mirrors the PUT's activate checks so the operator sees what is missing before the server refuses it
const getActivationBlockers = ({ settings, changes, hasUnpaidFrontedBudgetInvoice, brand }) => {
  const next = { invoiceCollection: 'auto_charge', ...settings, ...changes };
  const blockers = [];
  if (!brand?.stripeCustomerId) blockers.push('a Stripe customer on the brand');
  const canBeCharged = !!next.stripePaymentMethodId || (next.invoiceCollection === 'send_invoice' && Number(next.netPaymentTermsDays) > 0);
  if (!canBeCharged) blockers.push('a payment method or emailed invoices with net terms');
  if (next.feePercentage === null || next.feePercentage === undefined) blockers.push('a fee percentage');
  if (!(Number(next.monthlyLimitAmount) > 0)) blockers.push('a monthly limit above zero');
  if (next.payoutDelayDays === null || next.payoutDelayDays === undefined) blockers.push('a payout option');
  if (hasUnpaidFrontedBudgetInvoice) blockers.push('the unpaid fronted budget invoice settled');
  return blockers;
};

const toSetting = (field, value) => {
  if (NUMBER_FIELDS.includes(field)) return value === '' || value === null || value === undefined ? null : Number(value);
  else if (field === 'stripePaymentMethodId') return value || null;
  else if (field === 'paysThroughThirdPartyNetwork') return !!value;
  return value || 'auto_charge';
};

const formatAmount = amount => (amount === null || amount === undefined ? '—' : `$${Number(amount).toLocaleString(undefined, { maximumFractionDigits: 2 })}`); // prettier-ignore

// The admin axios interceptor rejects with the response itself, so the server's message sits on `data`
const getResponseErrorMessage = (e, fallback) => e?.data?.error || e?.data?.msg || e?.response?.data?.error || fallback;

export default OpportunityStrategyBillingCard;
